import { ColumnDef, SpreadsheetRowData } from "@components/spreadsheet/types";
import {
    ProfitAndLossResponse,
    ProfitAndLossSection,
    ReportPeriodWithLabel,
} from "@common/types/reports/types";
import { ProfitLossReportSummarySections } from "@common/dto/reports/reports-common.dto";
import { ProfitLossSectionsOrder } from "@components/accounting/profitLoss/constants";
import { profitLossSectionLabels } from "@common/types/profitLossCoa";
import { LABEL_FIELD, periodField } from "../shared/reportTaskFields";

export interface PLSpreadsheetRow extends SpreadsheetRowData {
    _accountCodes?: number[];
    /** Present only on counterparty child rows. `null` = unverified/empty counterparty. */
    _counterpartyId?: string | null;
    children?: PLSpreadsheetRow[];
}

type PlRow = NonNullable<ProfitAndLossSection["rows"]>[number];

function periodFields(data: PlRow["data"]): Record<string, number> {
    return Object.fromEntries(
        data.map((cell, idx) => [periodField(idx), cell.value]),
    );
}

/** Counterparty rows are skipped — they carry their parent account's code, not their own. */
function flattenAccountCodesFromRows(rows: PlRow[]): number[] {
    const codes: number[] = [];
    for (const row of rows) {
        if (row.kind !== "counterparty" && row.meta?.code !== undefined) {
            codes.push(row.meta.code);
        }
        if (row.children?.length) {
            codes.push(...flattenAccountCodesFromRows(row.children));
        }
    }
    return codes;
}

/**
 * Recursively collects every underlying account code covered by a section.
 * Sections that summarise other sections (e.g. "Net Income") expose their
 * members via `meta.summaryOfSections`; otherwise `section.rows` is the
 * authoritative list.
 */
function getAccountCodesFromSection(
    report: ProfitAndLossResponse,
    sectionData?: ProfitAndLossSection,
): number[] {
    if (!sectionData) {
        return [];
    }
    if (sectionData.rows) {
        return flattenAccountCodesFromRows(sectionData.rows);
    }
    if (sectionData.meta?.summaryOfSections) {
        return sectionData.meta.summaryOfSections.flatMap((id) =>
            getAccountCodesFromSection(report, report.sections[id]),
        );
    }
    return [];
}

/**
 * Builds the period columns for the P&L grid. Each column's cell renderer is
 * produced by `cellFnFactory`, which receives the period the column renders
 * so that per-period behaviour (e.g. drill-down) can close over it.
 */
export function buildPeriodColumns(
    periods: ReportPeriodWithLabel[],
    cellFnFactory: (
        period: ReportPeriodWithLabel,
        idx: number,
    ) => ColumnDef["cellFn"],
    className: ColumnDef["className"],
): ColumnDef[] {
    return periods.map((period, idx) => ({
        field: periodField(idx),
        label: period.label,
        isEditable: false,
        width: 140,
        cellFn: cellFnFactory(period, idx),
        className,
    }));
}

/** Mirrors the branching in `profitLossViewBuilder.ts` (`buildRow`). */
function buildRow(row: PlRow, path: string): PLSpreadsheetRow {
    if (row.kind === "group") {
        const groupAccountCodes = row.children?.length
            ? flattenAccountCodesFromRows(row.children)
            : [];
        const children: PLSpreadsheetRow[] = (row.children ?? []).map(
            (child, i) => buildRow(child, `${path}:group:${i}`),
        );
        if (children.length > 0) {
            children.push({
                _id: `total:group:${path}`,
                _isTotal: true,
                [LABEL_FIELD]: `Total ${row.category}`,
                _accountCodes: groupAccountCodes,
                ...periodFields(row.data),
            });
        }
        return {
            _id: `group:${path}`,
            [LABEL_FIELD]: row.category,
            _accountCodes: groupAccountCodes,
            ...(children.length > 0 ? { children } : {}),
            ...periodFields(row.data),
        };
    }

    const accountCodes = row.meta ? [row.meta.code] : [];
    const counterpartyRows = (row.children ?? []).filter(
        (child) => (child.kind ?? "counterparty") === "counterparty",
    );
    const counterpartyChildren: PLSpreadsheetRow[] | undefined =
        counterpartyRows.length > 0
            ? counterpartyRows.map((cp, j) => ({
                  _id: `cp:${path}:${cp.counterpartyId ?? "none"}:${j}`,
                  [LABEL_FIELD]: cp.counterpartyName ?? "No counterparty",
                  _accountCodes: accountCodes,
                  _counterpartyId: cp.counterpartyId ?? null,
                  ...periodFields(cp.data),
              }))
            : undefined;
    if (counterpartyChildren) {
        counterpartyChildren.push({
            _id: `total:row:${path}`,
            _isTotal: true,
            [LABEL_FIELD]: `Total ${row.category}`,
            _accountCodes: accountCodes,
            ...periodFields(row.data),
        });
    }

    return {
        _id: `row:${path}`,
        [LABEL_FIELD]: row.category,
        _accountCodes: accountCodes,
        ...(counterpartyChildren ? { children: counterpartyChildren } : {}),
        ...periodFields(row.data),
    };
}

export function buildSpreadsheetRows(
    report: ProfitAndLossResponse,
): PLSpreadsheetRow[] {
    const result: PLSpreadsheetRow[] = [];

    for (const sectionId of ProfitLossSectionsOrder) {
        const section = report.sections[sectionId];
        if (!section) {
            continue;
        }

        const label = profitLossSectionLabels[sectionId];
        const sectionAccountCodes = getAccountCodesFromSection(report, section);

        // Build one field per period for each data array
        const sectionValues = periodFields(section.data);

        const childRows: PLSpreadsheetRow[] = (section.rows ?? []).map(
            (row, i) => buildRow(row, `${sectionId}:${i}`),
        );

        if (childRows.length > 0) {
            childRows.push({
                _id: `total:${sectionId}`,
                _isTotal: true,
                [LABEL_FIELD]: `Total ${label}`,
                _accountCodes: sectionAccountCodes,
                ...sectionValues,
            });
            result.push({
                _id: `section:${sectionId}`,
                [LABEL_FIELD]: label,
                _accountCodes: sectionAccountCodes,
                children: childRows,
                ...sectionValues,
            });
        } else {
            result.push({
                _id: `section:${sectionId}`,
                _isTotal: ProfitLossReportSummarySections.includes(sectionId),
                [LABEL_FIELD]: label,
                _accountCodes: sectionAccountCodes,
                ...sectionValues,
            });
        }
    }
    return result;
}

/** True when a wrapper's children are its counterparty breakdown rather than sub-accounts/groups. */
function hasCounterpartyChildren(row: PLSpreadsheetRow): boolean {
    return (
        row.children?.some((child) => child._counterpartyId !== undefined) ??
        false
    );
}

/** Section and group wrappers expand by default; counterparty breakdowns stay collapsed. */
export function getDefaultExpandedIds(rows: PLSpreadsheetRow[]): string[] {
    const ids: string[] = [];
    const visit = (items: PLSpreadsheetRow[]): void => {
        for (const row of items) {
            if (!row.children?.length) {
                continue;
            }
            if (!hasCounterpartyChildren(row)) {
                ids.push(String(row._id));
            }
            visit(row.children);
        }
    };
    visit(rows);
    return ids;
}

/**
 * Any wrapper hides its own value while expanded — shown by its `Total …` child
 * instead — and reveals it when collapsed. Mirrors the standalone P&L's
 * `ProfitLossPeriodCell` (parents hide values when expanded).
 */
export function shouldHideExpandedRowValue(
    row: PLSpreadsheetRow,
    expandedIds: string[],
): boolean {
    return (
        (row.children?.length ?? 0) > 0 && expandedIds.includes(String(row._id))
    );
}

export interface CounterpartyFilter {
    counterpartiesIds?: string[];
    hasEmptyOrUnverifiedCounterparty?: boolean;
}

/**
 * Derives a counterparty filter from a PLSpreadsheetRow's `_counterpartyId`.
 * Returns `undefined` when the row has no counterparty dimension (i.e. it is
 * a category-level or section-level row rather than a counterparty child).
 */
export function buildCounterpartyFilter(
    row: PLSpreadsheetRow,
): CounterpartyFilter | undefined {
    const cpId = row._counterpartyId;
    if (cpId === undefined) {
        return undefined;
    }
    return {
        counterpartiesIds: typeof cpId === "string" ? [cpId] : undefined,
        hasEmptyOrUnverifiedCounterparty: cpId === null ? true : undefined,
    };
}
